import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';
import { subscribeToUnreadCount } from '../services/chatService';
import { useWindowSize, BP } from '../hooks/useWindowSize';
import {
  computeAverageTimeToHireDays,
  computeConversionRate,
  computeTotalSpendCents,
  countRecentApplicants,
  computeTopJobs,
} from '../utils/executiveStats';
function DashboardPage() {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const isTablet = width >= BP.mobile && width < BP.tablet;
  const user = getCurrentUser();
  const employerName = user?.displayName || user?.email?.split('@')[0] || 'there';

  const [stats, setStats] = useState({ activeJobs: 0, totalApplicants: 0, unreadMessages: 0, totalHired: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  // HC-EMPLOYER-002b: full application/payment docs (not just counts), used
  // to compute the executive stats section client-side — no new CFs.
  const [applications, setApplications] = useState([]);
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    if (!user) return;
    const uid = user.uid;
    const unsubs = [];

    // Active jobs — real-time count
    const jobsQ = query(collection(db, 'jobs'), where('employerId', '==', uid));
    unsubs.push(onSnapshot(jobsQ, (snap) => {
      const active = snap.docs.filter(d => d.data().status === 'active').length;
      setStats(prev => ({ ...prev, activeJobs: active }));
    }));

    // Total applicants — real-time count + full docs (executive stats)
    const appsQ = query(collection(db, 'applications'), where('employerId', '==', uid));
    unsubs.push(onSnapshot(appsQ, (snap) => {
      setStats(prev => ({ ...prev, totalApplicants: snap.size }));
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }));

    // Payments — real-time docs, feeds the executive stats spend total
    // (see utils/executiveStats.js's computeTotalSpendCents for the
    // status==='succeeded'-only filtering rationale)
    const paymentsQ = query(collection(db, 'payments'), where('employerId', '==', uid));
    unsubs.push(onSnapshot(paymentsQ, (snap) => {
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }));

    // Total hired — real-time count
    const hiredQuery = query(
      collection(db, 'applications'),
      where('employerId', '==', uid),
      where('status', '==', 'hired')
    );
    const unsubHired = onSnapshot(hiredQuery, (snap) => {
      setStats(prev => ({ ...prev, totalHired: snap.size }));
    });

    // Unread count — real-time listener (updates when employer reads a conversation)
    const unsubscribeUnread = subscribeToUnreadCount(uid, (count) => {
      setStats(prev => ({ ...prev, unreadMessages: count }));
    });

    // Conversations — drives recent conversations
    const convsQ = query(collection(db, 'conversations'), where('employerId', '==', uid));
    unsubs.push(onSnapshot(convsQ, async (snap) => {
      // Recent conversations: sort by lastMessageTime desc, take top 5
      const sorted = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.lastMessageTime)
        .sort((a, b) => b.lastMessageTime.toMillis() - a.lastMessageTime.toMillis())
        .slice(0, 5);

      // Fetch worker display names — HC-INFRA-008C, Prompt #422: reads the
      // public projection (publicWorkerProfiles/{uid}, Prompt #419), not
      // the raw users/{uid} doc. displayName/photoURL are the only name/
      // photo fields this projection ever carries (no email) —
      // missing-projection falls back to 'Unknown Worker', matching the
      // existing not-found handling below.
      const activity = await Promise.all(
        sorted.map(async (conv) => {
          let workerName = 'Unknown Worker';
          let workerPhoto = null;
          if (conv.candidateId) {
            try {
              const workerDoc = await getDoc(doc(db, 'publicWorkerProfiles', conv.candidateId));
              if (workerDoc.exists()) {
                const d = workerDoc.data();
                workerName = d.displayName || 'Unknown Worker';
                workerPhoto = d.photoURL || null;
              }
            } catch (_) {}
          }
          return {
            id: conv.id,
            workerName,
            workerPhoto,
            jobTitle: conv.jobTitle || 'Job',
            lastMessage: typeof conv.lastMessage === 'string'
              ? conv.lastMessage
              : conv.lastMessage?.text || '',
            lastMessageTime: conv.lastMessageTime,
          };
        })
      );

      setRecentActivity(activity);
    }));

    return () => {
      unsubs.forEach(u => u());
      unsubscribeUnread();
      unsubHired();
    };
  }, [user]);

  // HC-EMPLOYER-002b: executive stats, all pure client-side aggregation
  // over the applications/payments docs already streamed above.
  const executiveStats = useMemo(() => ({
    avgTimeToHireDays: computeAverageTimeToHireDays(applications),
    conversionRate: computeConversionRate(applications),
    totalSpendCents: computeTotalSpendCents(payments),
    newApplicants7d: countRecentApplicants(applications, 7),
    newApplicants30d: countRecentApplicants(applications, 30),
    topJobs: computeTopJobs(applications, 3),
  }), [applications, payments]);

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <>

        {/* Welcome */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px 0' }}>
            Welcome back, {employerName}!
          </h2>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '13px' }}>
            Here's what's happening with your jobs today.
          </p>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '1rem',
          marginBottom: '2rem',
        }}>
          <StatCard
            label="Active Jobs"
            value={stats.activeJobs}
            color="#E8590C"
            onClick={() => navigate('/manage-jobs')}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>}
          />
          <StatCard
            label="Total Applicants"
            value={stats.totalApplicants}
            color="#0F1E2E"
            onClick={() => navigate('/applicants')}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
          />
          <StatCard
            label="Total Hired"
            value={stats.totalHired}
            color="#16A34A"
            onClick={() => navigate('/hired')}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
          />
          <StatCard
            label="Unread Messages"
            value={stats.unreadMessages}
            color={stats.unreadMessages > 0 ? '#ef4444' : '#6b7280'}
            onClick={() => navigate('/messages')}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
          />
        </div>

        {/* Performance Overview — HC-EMPLOYER-002b executive stats, all
            computed client-side from the applications/payments docs above */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 0.75rem 0', color: '#374151' }}>
            Performance Overview
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '1rem',
          }}>
            <ExecutiveStatCard
              label="Avg. Time to Hire"
              value={executiveStats.avgTimeToHireDays == null
                ? 'No hires yet'
                : `${executiveStats.avgTimeToHireDays.toFixed(1)} days`}
            />
            <ExecutiveStatCard
              label="Application → Hire Rate"
              value={executiveStats.conversionRate == null
                ? 'No applicants yet'
                : `${executiveStats.conversionRate.toFixed(1)}%`}
            />
            <ExecutiveStatCard
              label="Total Spend"
              value={`$${(executiveStats.totalSpendCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              caption={executiveStats.totalSpendCents === 0 ? 'No spend yet' : 'Completed hire payments'}
            />
            <ExecutiveStatCard
              label="New Applicants"
              value={`${executiveStats.newApplicants7d} this week`}
              caption={`${executiveStats.newApplicants30d} in the last 30 days`}
            />
          </div>

          {/* Top Performing Job Posts */}
          <div style={{
            backgroundColor: '#F8FAFC',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
            padding: '0.875rem 1rem',
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Top Performing Job Posts
            </p>
            {executiveStats.topJobs.length === 0 ? (
              <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                Post a job to see performance here.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {executiveStats.topJobs.map((job, i) => (
                  <div
                    key={job.jobId}
                    onClick={() => navigate(`/jobs/${job.jobId}/applicants`)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', cursor: 'pointer', padding: '2px 0' }}
                  >
                    <span style={{ color: '#374151' }}>
                      <span style={{ color: '#9ca3af', marginRight: '6px' }}>{i + 1}.</span>
                      {job.jobTitle}
                    </span>
                    <span style={{ color: '#6b7280', fontWeight: '600', flexShrink: 0, marginLeft: '0.75rem' }}>
                      {job.count} applicant{job.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Conversations */}
        <div style={{
          backgroundColor: '#F8FAFC',
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Recent Conversations</h3>
            <span
              onClick={() => navigate('/messages')}
              style={{ color: '#E8590C', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
            >
              View all →
            </span>
          </div>

          {recentActivity.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              No conversations yet. Post a job to get started!
            </div>
          ) : (
            recentActivity.map((item, i) => (
              <div
                key={item.id}
                onClick={() => navigate(`/chat/${item.id}`)}
                style={{
                  padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
                  borderBottom: i < recentActivity.length - 1 ? '1px solid #f3f4f6' : 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: '0.75rem',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#E8590C',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: 'white',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}>
                  {item.workerPhoto ? (
                    <img src={item.workerPhoto} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                  ) : (
                    <span>{(item.workerName || '?')[0].toUpperCase()}</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    <span style={{ fontWeight: '600', fontSize: '15px', flexShrink: 0 }}>{item.workerName}</span>
                    <span style={{ color: '#d1d5db', flexShrink: 0 }}>·</span>
                    <span style={{ color: '#6b7280', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{item.jobTitle}</span>
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#9ca3af',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {item.lastMessage || 'No messages yet'}
                  </p>
                </div>
                <span style={{
                  fontSize: '13px',
                  color: '#9ca3af',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  {formatRelativeTime(item.lastMessageTime)}
                </span>
              </div>
            ))
          )}
        </div>

    </>
  );
}

function StatCard({ label, value, color, icon, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: '#F8FAFC',
        padding: '0.75rem 1rem',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
        borderLeft: `3px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        gap: '0.875rem',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={onClick ? (e => e.currentTarget.style.backgroundColor = '#f1f5f9') : undefined}
      onMouseLeave={onClick ? (e => e.currentTarget.style.backgroundColor = '#F8FAFC') : undefined}
    >
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: color + '18',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: color,
      }}>
        {icon}
      </div>
      <div>
        <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </p>
        <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827', lineHeight: 1 }}>
          {value}
        </p>
      </div>
    </div>
  );
}

// HC-EMPLOYER-002b: smaller/plainer than StatCard (no icon, no left accent
// bar) since these 4 executive-stat tiles are a compact v1 addition, not
// meant to visually compete with the primary 4 top-of-dashboard metrics.
function ExecutiveStatCard({ label, value, caption }) {
  return (
    <div style={{
      backgroundColor: '#F8FAFC',
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
    }}>
      <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </p>
      <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827', lineHeight: 1.2 }}>
        {value}
      </p>
      {caption && (
        <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
          {caption}
        </p>
      )}
    </div>
  );
}

export default DashboardPage;
